import styled, { keyframes, css } from 'styled-components';

import { Card } from '@xing-com/card';
import {
  spaceM,
  spaceXXL,
  xdlColorSelected,
  cornerRadiusXL,
  scale010,
  scale005,
  spaceXXS,
} from '@xing-com/tokens';

import {
  CARD_WIDTH,
  CARD_GAP,
  SAVED_CARDS_ITEM_WIDTH,
  SAVED_CARDS_ITEM_HEIGHT,
} from './constants';

const Container = styled.div<{ $totalDecks: number; $hide?: boolean }>`
  display: flex;
  width: ${(props) =>
    props.$totalDecks * CARD_WIDTH + (props.$totalDecks - 1) * CARD_GAP}px;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: ${spaceXXL};

  transition: max-height 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: ${(props) => (props.$hide ? '0' : '300px')};
  overflow: hidden;
`;

const scaleAndGrow = keyframes`
0% {
  width: 0;
  min-width: 0;
  transform: scale(0);
}
to {
  width: ${SAVED_CARDS_ITEM_WIDTH};
  min-width: ${SAVED_CARDS_ITEM_WIDTH};
  transform: scale(1);
}
`;

const scaleAndShrink = keyframes`
  from {
    width: ${SAVED_CARDS_ITEM_WIDTH};
    min-width: ${SAVED_CARDS_ITEM_WIDTH};
    transform: scale(1);
  }
  to {
    width: 0;
    min-width: 0;
    padding: 0;
    margin: 0;
    transform: scale(0);
    display: none;
  }
`;

const growAnimationCss = css`
  width: 0;
  transform: scale(0);
  animation-name: ${scaleAndGrow};
  animation-duration: 0.5s;
  animation-timing-function: ease-out;
  animation-fill-mode: forwards;
`;

const shrinkAnimationCss = css`
  width: ${SAVED_CARDS_ITEM_WIDTH};
  transform: scale(1);
  animation-name: ${scaleAndShrink};
  animation-duration: 0.5s;
  animation-timing-function: ease-in;
  animation-fill-mode: forwards;
`;

const Item = styled(Card)<{ $isNew?: boolean; $isDeleting?: boolean }>`
  width: ${SAVED_CARDS_ITEM_WIDTH};
  min-width: ${SAVED_CARDS_ITEM_WIDTH};

  height: ${SAVED_CARDS_ITEM_HEIGHT};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${spaceM};
  transform-origin: left;
  cursor: pointer;

  & > * {
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  &:hover {
    &::after {
      border-color: ${xdlColorSelected};
      border-radius: ${cornerRadiusXL};
      border-style: solid;
      border-width: calc(${scale010} + ${scale005});
      content: '';
      inset: -${spaceXXS};
      pointer-events: none;
      position: absolute;
    }
  }

  ${({ $isNew }) => ($isNew ? growAnimationCss : '')}
  ${({ $isDeleting }) => ($isDeleting ? shrinkAnimationCss : '')}
`;

const List = styled.div`
  display: flex;
  width: 100%;
  gap: ${CARD_GAP}px;
  overflow: hidden;
`;

const ListContainer = styled.div`
  width: 100%;
`;

const ItemContent = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
`;

const EmptyState = styled(Card)`
  display: none;
`;

export { Container, List, Item, ListContainer, ItemContent, EmptyState };
